<shared-ui-card>
   <shared-ui-card-header
      [buttonActions]="descriptionsCardActions()"
      [title]="'mediaplan_approval.title' | translate"
   ></shared-ui-card-header>

   <shared-ui-card-content>
      <div class="flex flex-col gap-4">
         @if (approvalRequestStore.loading() && !approvalRequestStore.entities().length) {
            <shared-ui-skeleton-loader
               [count]="1"
               [skeletonConfig]="skeletonLoadConfig"
               [type]="skeletonType"
            ></shared-ui-skeleton-loader>
         } @else if (!approvalRequestStore.entities().length) {
            <div class="flex flex-auto items-center justify-center">
               <div [matTooltip]="createButtonSettings().tooltip | translate">
                  <button
                     *webvisAclRestrict="approvalCreateRule"
                     (click)="openApprovalDialog()"
                     [disabled]="createButtonSettings().disabled"
                     [loading]="approvalRequestStore.loading()"
                     [ngClass]="createButtonSettings().disabled ? 'bg-surface-container' : 'bg-primary text-onPrimary'"
                     class="h-12 rounded-full"
                     mat-flat-button
                  >
                     {{ 'mediaplan_approval.create_approval' | translate }}
                  </button>
               </div>
            </div>
         } @else {
            <div class="flex items-center justify-between bg-surface-container p-4 rounded-md border border-outline md:gap-48 gap-8">
               <shared-ui-dropdown-input
                  (selectionChange)="onVersionChange($event)"
                  [data]="approvalsDropdownData()"
                  [inputFormControl]="approvalRequestControl"
                  [itemTemplate]="approvalsDropdownItem"
                  [loading]="approvalRequestStore.loading()"
                  class="flex-auto max-w-lg -mb-1"
                  textField="displayName"
                  type="single"
                  valueField="id"
               ></shared-ui-dropdown-input>

               <div class="flex items-center gap-2">
                  <span>{{ 'mediaplan_approval.status' | translate }}:</span>
                  <webvis-feature-approval-status-badge [approvalStatus]="currentApproval().status" />
                  @if (attachmentId() !== null) {
                     <webvis-feature-share-link-indicator
                        [targetConfig]="shareLinkTargetConfig"
                        [targetId]="attachmentId()"
                     />
                  }
               </div>
            </div>

            <div class="flex flex-auto items-center justify-center">
               @if (hasDriveLink()) {
                  <button
                     (click)="openApprovalRequest()"
                     [disabled]="approvalRequestStore.loading()"
                     class="h-12 rounded-full md:w-1/2 w-full"
                     mat-flat-button
                  >
                     {{ 'mediaplan_approval.open_deck' | translate }}
                  </button>
               } @else {
                  <button
                     class="bg-surface-container text-onSurface h-12 rounded-full cursor-default"
                     disabled
                     mat-flat-button
                  >
                     <fa-icon
                        [icon]="['fab', 'google-drive']"
                        class="mr-2"
                     ></fa-icon>
                     {{ 'mediaplan_approval.no_drive_link' | translate }}
                  </button>
               }
            </div>

            <div class="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
               <div class="flex items-center gap-2">
                  <fa-icon
                     [icon]="['fak', 'spoedicon-filled']"
                     class="text-primary"
                  ></fa-icon>
                  {{ deadlineTranslationString() | translate }}
               </div>
               <div class="">{{ (currentApproval()?.deadline | customDate: 'date') || '-' }}</div>

               @if (currentApproval()?.reviewers?.length) {
                  <div class="flex items-center gap-2">
                     <fa-icon
                        [icon]="['fas', 'user']"
                        class="text-extended"
                     ></fa-icon>
                     {{ 'mediaplan_approval.approved_by' | translate }}
                  </div>
                  <div class="flex gap-2">
                     @for (reviewer of approvedBy(); track $index) {
                        <ng-template
                           [ngTemplateOutlet]="reviewerName"
                           [ngTemplateOutletContext]="{ $implicit: reviewer, last: $last }"
                        ></ng-template>
                     } @empty {
                        <span>-</span>
                     }
                  </div>
                  <div class="flex items-center gap-2">
                     <fa-icon
                        [icon]="['fas', 'user']"
                        class="text-error"
                     ></fa-icon>
                     {{ 'mediaplan_approval.rejected_by' | translate }}
                  </div>
                  <div class="flex gap-2">
                     @for (reviewer of rejectedBy(); track $index) {
                        <ng-template
                           [ngTemplateOutlet]="reviewerName"
                           [ngTemplateOutletContext]="{ $implicit: reviewer, last: $last }"
                        ></ng-template>
                     } @empty {
                        <span>-</span>
                     }
                  </div>
                  <div class="flex items-center gap-2">
                     <fa-icon
                        [icon]="['fas', 'user']"
                        class="text-primary"
                     ></fa-icon>
                     {{ 'mediaplan_approval.approval_pending' | translate }}
                  </div>
                  <div class="flex gap-2">
                     @for (reviewer of approvalPending(); track $index) {
                        <ng-template
                           [ngTemplateOutlet]="reviewerName"
                           [ngTemplateOutletContext]="{ $implicit: reviewer, last: $last }"
                        ></ng-template>
                     } @empty {
                        <span>-</span>
                     }
                  </div>
               }
            </div>
         }
      </div>
   </shared-ui-card-content>

   @if (approvalRequestStore.entities().length) {
      <shared-ui-card-actions>
         <button
            *webvisAclRestrict="cancelPermission()"
            (click)="cancelApproval()"
            [disabled]="approvalRequestStore.loading()"
            [ngClass]="!approvalRequestStore.loading() ? 'bg-error text-onError' : ''"
            mat-button
         >
            <fa-icon
               [icon]="['fas', 'xmark']"
               class="mr-2"
            ></fa-icon>
            {{ 'approval.cancel' | translate }}
         </button>
         <button
            *webvisAclRestrict="submitPermission()"
            (click)="sendNotification()"
            [loading]="approvalRequestStore.loading()"
            class="px-6 rounded-full"
            mat-flat-button
         >
            <fa-icon
               [icon]="['fas', 'bell']"
               class="mr-2"
            ></fa-icon>
            {{ 'mediaplan_approval.notify_client' | translate: { client: mediaplan()?.client.organisation } }}
         </button>
         @if (currentApproval()?.status.slug === 'cancelled') {
            <button
               *webvisAclRestrict="editPermission()"
               (click)="reinstate()"
               [loading]="approvalRequestStore.loading()"
               class="px-6 rounded-full"
               mat-flat-button
            >
               <fa-icon
                  [icon]="['fas', 'undo']"
                  class="mr-2"
               ></fa-icon>
               {{ 'mediaplan_approval.reinstate' | translate }}
            </button>
         }
         <ng-container *webvisAclRestrict="reviewPermission()">
            <button
               (click)="openApprovalRequest(true)"
               [loading]="approvalRequestStore.loading()"
               [ngClass]="!approvalRequestStore.loading() ? 'bg-extendedSafeContainer text-extendedOnSafeContainer' : ''"
               class="px-6 rounded-full"
               mat-flat-button
            >
               <fa-icon
                  [icon]="['fas', 'check']"
                  class="mr-2"
               ></fa-icon>
               {{ 'general.approve' | translate }}
            </button>
            <button
               (click)="openApprovalRequest(false)"
               [loading]="approvalRequestStore.loading()"
               [ngClass]="!approvalRequestStore.loading() ? 'bg-error text-onError' : ''"
               class="px-6 rounded-full"
               mat-flat-button
            >
               <fa-icon
                  [icon]="['fas', 'xmark']"
                  class="mr-2"
               ></fa-icon>
               {{ 'general.reject' | translate }}
            </button>
         </ng-container>
      </shared-ui-card-actions>
   }
</shared-ui-card>

<ng-template
   #reviewerName
   let-last="last"
   let-reviewer
>
   <shared-ui-initials-avatar
      [name]="reviewer.user | fullName"
      size="sm"
   />
</ng-template>

<ng-template
   #approvalsDropdownItem
   let-request
>
   {{ request.displayName }} - <span class="text-muted text-sm">{{ 'approval.status_' + request.status.slug | translate }}</span>
</ng-template>
